import React, { MouseEvent } from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import Lock from '@COMPONENTS/SHARED/Lock';

import { ReactComponent as AddPlusIcon } from '@ICONS/add-plus-icon.svg';
import { ReactComponent as PreviewIcon } from '@ICONS/preview-icon.svg';
import { ReactComponent as EditIcon } from '@ICONS/edit-icon-2.svg';
import { ReactComponent as SettingsIcon } from '@ICONS/settings-icon.svg';
import { ReactComponent as ClockIcon } from '@ICONS/clock-icon.svg';
import { ReactComponent as GridIcon } from '@ICONS/grid-icon.svg';
import { ReactComponent as ListIcon } from '@ICONS/list-icon.svg';
import { ReactComponent as MandatoryIcon } from '@ICONS/mandatory-icon.svg';
import { ReactComponent as PageBreckIcon } from '@ICONS/page-breck-icon.svg';
import { ReactComponent as LandscapeIcon } from '@ICONS/landscape-icon.svg';
import { ReactComponent as DeleteIcon } from '@ICONS/delete-icon.svg';
import Tooltip from '@COMPONENTS/SHARED/Tooltip';
import styles from './AddButton.module.scss';

const cx: CX = classnames.bind(styles);

const icons: { [key: string]: any } = {
    plus: AddPlusIcon,
    preview: PreviewIcon,
    edit: EditIcon,
    settings: SettingsIcon,
    clock: ClockIcon,
    grid: GridIcon,
    list: ListIcon,
    mandatory: MandatoryIcon,
    pagebreck: PageBreckIcon,
    landscape: LandscapeIcon,
    delete: DeleteIcon,
};

function AddButton(props: Props) {
    const {
        title, fontSize, onClick, locked, size, icon, iconType, tooltipTitle, isActive,
    } = props;

    const [tooltipId] = React.useState(tooltipTitle ? Tooltip.getId() : '');
    const IconComponent = iconType ? icons[iconType] : null;

    function handleClick(event: MouseEvent<HTMLButtonElement>) {
        if (!locked) {
            onClick(event);
        }
    }

    return (
        <div
            className={cx('add-button', {
                locked,
                small: size === 'small',
                active: isActive,
            })}
        >
            {
                locked
                && (
                    <div className={cx('lock-wrapper')}>
                        <Lock size={size === 'small' ? 'small' : 'large'} />
                    </div>
                )
            }

            <button
                type="button"
                className="clear-button"
                id={tooltipId}
                style={{
                    fontSize,
                }}
                onClick={(e) => handleClick(e)}
            >
                {icon && IconComponent && <IconComponent />}
                {
                    title && <div className={cx('title', 'custom-btn-title')}>{title}</div>
                }
                {!icon && (
                    <div className={cx('plus')}>
                        +
                    </div>
                )}
            </button>
            
            {tooltipTitle && (
                <Tooltip
                    id={tooltipId}
                    content={tooltipTitle}
                    disabled={false}
                />
            )}
        </div>
    );
}

AddButton.defaultProps = {
    title: '',
    size: 'large',
    fontSize: 14,
    icon: false,
    iconType: 'plus',
    tooltipTitle: '',
    isActive: false,
};

AddButton.propTypes = {
    locked: PropTypes.bool.isRequired,
    size: PropTypes.oneOf(['small', 'large']),
    title: PropTypes.string,
    fontSize: PropTypes.number,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.bool,
    iconType: PropTypes.oneOf(['plus', 'preview', 'edit', 'settings', 'clock', 'grid', 'list', 'mandatory', 'pagebreck', 'landscape', 'delete']),
    tooltipTitle: PropTypes.string,
    isActive: PropTypes.bool,
};

type Props = InferProps<typeof AddButton.propTypes> & typeof AddButton.defaultProps;

export default AddButton;
